import { type PointerEvent, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { ChartPoint } from '@/lib/stats';
import { cn } from '@/lib/utils';

/**
 * One series over time, as columns from a shared baseline. The readout above follows the
 * pointer, a finger or the arrow keys, and rests on the peak; the table below holds every
 * value, so none of them depends on hovering.
 */
export function ColumnChart(props: { readonly title: string; readonly points: ReadonlyArray<ChartPoint>; readonly unit: string }) {
  const { title, points, unit } = props;
  const [selected, setSelected] = useState<number | undefined>(undefined);
  if (points.length === 0) {
    return <p className="text-sm text-muted-foreground">Nothing to chart yet.</p>;
  }
  const peak = points.reduce((highest, point) => (point.value > highest.value ? point : highest));
  const describe = (point: ChartPoint) => `${point.label}: ${point.value.toLocaleString()} ${unit}`;
  const current = selected === undefined ? undefined : points[selected];
  const follow = (event: PointerEvent<HTMLDivElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    setSelected(Math.max(0, Math.min(points.length - 1, Math.floor(((event.clientX - box.left) / box.width) * points.length))));
  };
  const axis = [points[0], points[Math.floor(points.length / 2)], points[points.length - 1]];

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground tabular-nums">{current === undefined ? `Peak · ${describe(peak)}` : describe(current)}</p>
      <div
        role="slider"
        tabIndex={0}
        aria-label={title}
        aria-valuemin={0}
        aria-valuemax={points.length - 1}
        aria-valuenow={selected ?? points.indexOf(peak)}
        aria-valuetext={describe(current ?? peak)}
        className="relative h-40 touch-pan-y cursor-crosshair rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onPointerMove={follow}
        onPointerDown={follow}
        // A finger lifting also leaves; the value it landed on stays up to be read.
        onPointerLeave={(event) => (event.pointerType === 'mouse' ? setSelected(undefined) : undefined)}
        onBlur={() => setSelected(undefined)}
        onKeyDown={(event) => {
          const step = event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1 : 0;
          if (step !== 0) {
            event.preventDefault();
            setSelected(Math.max(0, Math.min(points.length - 1, (selected ?? points.indexOf(peak)) + step)));
          }
        }}
      >
        <div className="absolute inset-x-0 top-0 border-t border-border" />
        <div className="absolute inset-0 flex items-end border-b border-border">
          {points.map((point, index) => (
            <div key={point.key} className="flex h-full min-w-0 flex-1 items-end justify-center px-px">
              <div
                className={cn('w-full max-w-6 rounded-t bg-primary transition-opacity', selected !== undefined && selected !== index && 'opacity-40')}
                style={{ height: `${(point.value / Math.max(peak.value, 1)) * 100}%`, minHeight: point.value > 0 ? 2 : 0 }}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-between text-xs text-muted-foreground tabular-nums">
        {axis.map((point, index) => (
          <span key={`${point.key}-${index}`}>{point.label}</span>
        ))}
      </div>
      <details className="text-sm">
        <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">As a table</summary>
        <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{title}</TableHead>
                <TableHead className="text-right">{unit}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {points.map((point) => (
                <TableRow key={point.key}>
                  <TableCell>{point.label}</TableCell>
                  <TableCell className="text-right tabular-nums">{point.value.toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </details>
    </div>
  );
}
